import React from "react";
import styled from "styled-components";
import { useLocation } from "react-router-dom";

import { Icon } from "../components/Icon";
import { waLink } from "../data/diagnostico";
import { PEDIDO_KEY } from "../components/FormularioDiagnostico";
import { setMetaTag, restoreDefaultMeta } from "../utils/pageMeta";

import LOGO from "../assets/media/logo_nav.png";

const PEDIDO_TTL_MS = 24 * 60 * 60 * 1000;

function lerUltimoPedido() {
    try {
        const cru = window.sessionStorage.getItem(PEDIDO_KEY);
        if (!cru) return null;
        const pedido = JSON.parse(cru);
        if (!pedido || !pedido.em || Date.now() - pedido.em > PEDIDO_TTL_MS) return null;
        return pedido;
    } catch (error) {
        return null;
    }
}

const PASSOS = [
    { n: "1", texto: "Uma pessoa do time lê o que você escreveu — não é resposta automática." },
    { n: "2", texto: "Conversa de 20 minutos, no horário que der para você." },
    { n: "3", texto: "Análise dos requisitos, estimativa de esforço e proposta com valor por escrito." }
];

const Page = styled.main`
    min-height: 100svh;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 28px 16px;
    background: var(--bg);
`;

const Panel = styled.section`
    width: min(680px, 100%);
    display: flex;
    flex-direction: column;
    gap: 22px;
    padding: clamp(28px, 5vw, 42px);
    border-radius: 22px;
    background: #ffffff;
    border: 1px solid var(--line);

    @media only screen and (max-width: 600px) {
        gap: 18px;
        padding: 24px;
        border-radius: 18px;
    }
`;

const LogoLink = styled.a`
    display: inline-flex;
    width: fit-content;
`;

const Logo = styled.img`
    width: 100px;
    height: auto;
`;

const IconWrap = styled.div`
    width: 54px;
    height: 54px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 999px;
    background: var(--success-bg);
    color: var(--success-strong);
`;

const Title = styled.h1`
    margin: 0;
    font-size: clamp(1.9rem, 4vw, 2.5rem);
    font-weight: 800;
    line-height: 1.14;
    letter-spacing: -0.025em;
    color: var(--ink);
    text-wrap: balance;
`;

const Lead = styled.p`
    margin: 0;
    font-size: 16.5px;
    font-weight: 500;
    line-height: 1.62;
    color: var(--muted);

    strong { color: var(--ink); }
`;

const StepsGrid = styled.div`
    display: grid;
    gap: 10px;
`;

const StepRow = styled.div`
    display: grid;
    grid-template-columns: 30px minmax(0, 1fr);
    gap: 12px;
    align-items: center;
    padding: 14px 16px;
    border-radius: 12px;
    background: var(--bg-soft);
    border: 1px solid var(--line);
`;

const StepIndex = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    border-radius: 999px;
    background: var(--brand);
    color: #fff;
    font-size: 13px;
    font-weight: 800;
`;

const StepText = styled.span`
    font-size: 15px;
    font-weight: 600;
    line-height: 1.5;
    color: var(--ink);
`;

const Actions = styled.div`
    display: flex;
    gap: 12px;
    flex-wrap: wrap;

    @media only screen and (max-width: 600px) {
        width: 100%;
    }
`;

const PrimaryLink = styled.a`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    min-height: 54px;
    padding: 0 22px;
    border-radius: 12px;
    color: #ffffff;
    font-weight: 800;
    background: var(--brand);

    &:hover { background: var(--brand-strong); }

    @media only screen and (max-width: 600px) {
        width: 100%;
    }
`;

const SecondaryLink = styled.a`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 54px;
    padding: 0 22px;
    border-radius: 12px;
    color: var(--ink);
    background: var(--bg-soft);
    border: 1px solid var(--line);
    font-weight: 700;

    @media only screen and (max-width: 600px) {
        width: 100%;
    }
`;

const FinePrint = styled.p`
    margin: 0;
    font-size: 12.5px;
    font-weight: 600;
    line-height: 1.55;
    color: var(--muted-soft);
`;

function ThankYou() {
    const location = useLocation();
    const pedido = (location.state && (location.state.nome || location.state.telefone))
        ? location.state
        : (lerUltimoPedido() || {});
    const nome = pedido.nome || "";
    const telefone = pedido.telefone || "";
    const situacao = pedido.situacao || "";

    React.useEffect(() => {
        document.title = "Pedido recebido · Mango Software House";
        setMetaTag('meta[name="robots"]', { name: "robots", content: "noindex, nofollow" });

        return () => {
            restoreDefaultMeta();
        };
    }, []);

    const waConfirma = waLink(situacao
        ? `Olá! Acabei de enviar o pedido de diagnóstico pelo site. Situação: ${situacao}.`
        : "Olá! Acabei de enviar o pedido de diagnóstico pelo site.");

    return (
        <Page>
            <Panel>
                <LogoLink href="/" aria-label="Voltar para a página inicial da Mango">
                    <Logo src={LOGO} alt="Mango Software House" />
                </LogoLink>

                <IconWrap>
                    <Icon name="task_alt" size="31px" />
                </IconWrap>

                <div>
                    <Title>{nome ? `Pedido recebido, ${nome}. Agora é com a gente.` : "Pedido recebido. Agora é com a gente."}</Title>
                    {telefone && situacao ? (
                        <Lead style={{ marginTop: "10px" }}>
                            Guardamos o seu caso como <strong>{situacao}</strong> e vamos responder no <strong>{telefone}</strong> em até 1 dia útil.
                        </Lead>
                    ) : (
                        <Lead style={{ marginTop: "10px" }}>
                            Recebemos o seu pedido de diagnóstico. Uma pessoa do nosso time vai analisar e responder pelo telefone ou e-mail que você informou, em até 1 dia útil.
                        </Lead>
                    )}
                </div>

                <StepsGrid>
                    {PASSOS.map((passo) => (
                        <StepRow key={passo.n}>
                            <StepIndex>{passo.n}</StepIndex>
                            <StepText>{passo.texto}</StepText>
                        </StepRow>
                    ))}
                </StepsGrid>

                <Actions>
                    <PrimaryLink href={waConfirma} target="_blank" rel="noreferrer">
                        <Icon name="chat" size="20px" />
                        Falar agora no WhatsApp
                    </PrimaryLink>

                    <SecondaryLink href="/">
                        Voltar ao site
                    </SecondaryLink>
                </Actions>

                <FinePrint>Sem lista de e-mail e sem compartilhar com terceiros — detalhes na <a href="/privacidade" style={{ color: "var(--brand-2)", fontWeight: 700 }}>política de privacidade</a>.</FinePrint>
            </Panel>
        </Page>
    );
}

export { ThankYou };
export default ThankYou;
